import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, Table, Tooltip } from "antd";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiDownload, FiCalendar, FiFileText, FiAlertCircle,
  FiChevronDown, FiRefreshCw, FiHelpCircle,
} from "react-icons/fi";
import SidebarNav from "../../sidebar";
import FilterPanel from "../../commoncomponent/FilterPanel";
import { getFiuMonthlySummaryApi, exportFiuMonthlyReportApi } from "../../../../service/api";
import { saveBlobAsFile } from "../shared";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const FiuMonthlyReports = () => {
  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // ── Year overview state ──────────────────────────────────────────────────
  const [year, setYear]               = useState(currentYear);
  const [months, setMonths]           = useState([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [downloadingMonth, setDownloadingMonth] = useState(null);

  // ── Custom range state ───────────────────────────────────────────────────
  const [startDate, setStartDate]     = useState("");
  const [endDate, setEndDate]         = useState("");
  const [strs, setStrs]               = useState(0);
  const [priorityStrs, setPriorityStrs] = useState(0);
  const [typologyStrs, setTypologyStrs] = useState(0);
  const [alertsCount, setAlertsCount] = useState(0);
  const [generatingCustom, setGeneratingCustom] = useState(false);

  // ── Load monthly summary for the selected year ────────────────────────
  const loadMonths = async () => {
    setLoadingMonths(true);
    try {
      const res = await getFiuMonthlySummaryApi({ year });
      if (res?.status && Array.isArray(res?.data)) {
        setMonths(res.data);
      } else {
        setMonths([]);
      }
    } catch {
      setMonths([]);
      toast.error("Failed to load monthly summary", { toastId: 'fiu-monthly-load-error' });
    } finally {
      setLoadingMonths(false);
    }
  };

  useEffect(() => {
    loadMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // ── Download a specific month's report ─────────────────────────────────
  const handleMonthDownload = async (month) => {
    setDownloadingMonth(month);
    try {
      const res = await exportFiuMonthlyReportApi({ year, month });
      const label = `${MONTH_NAMES[month - 1]}_${year}`;
      saveBlobAsFile(res, `FIU_VASP_${label}.xlsx`);
      toast.success(`Downloading FIU Report for ${MONTH_NAMES[month - 1]} ${year}`, { toastId: 'fiu-monthly-download-success' });
    } catch {
      toast.error("Download failed. Please try again.", { toastId: 'download-failed' });
    } finally {
      setDownloadingMonth(null);
    }
  };

  // ── Generate & download custom date-range report ────────────────────────
  const handleCustomDownload = async () => {
    if (!startDate || !endDate) {
      toast.warning("Please select both start date and end date.", { toastId: 'fiu-monthly-validation-warning' });
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.warning("Start date must be before end date.", { toastId: 'fiu-monthly-validation-warning' });
      return;
    }
    setGeneratingCustom(true);
    try {
      const res = await exportFiuMonthlyReportApi({
        startDate, endDate, strs, priorityStrs, typologyStrs, alertsCount,
      });
      saveBlobAsFile(res, `FIU_VASP_${startDate}_to_${endDate}.xlsx`);
      toast.success("Custom FIU Report download started", { toastId: 'fiu-custom-report-success' });
    } catch {
      toast.error("Failed to generate report. Please try again.", { toastId: 'fiu-custom-report-error' });
    } finally {
      setGeneratingCustom(false);
    }
  };

  // ── Monthly table columns ────────────────────────────────────────────────
  const columns = [
    {
      title: "#",
      key: "idx",
      width: 48,
      render: (_, __, i) => <span style={{ color: "#8a94a6", fontSize: 12 }}>{i + 1}</span>,
    },
    {
      title: "Month",
      dataIndex: "label",
      key: "label",
      render: (v, r) => (
        <span style={{ fontWeight: 700, color: r.isCurrent ? "#fd7e14" : "#1a1a2e" }}>
          {v} {year}
          {r.isCurrent && (
            <span style={{ marginLeft: 6, fontSize: 10, background: "#fff3cd", color: "#856404", padding: "2px 7px", borderRadius: 10, fontWeight: 600 }}>
              Current Month
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, r) => {
        const m = String(r.month).padStart(2, "0");
        return (
          <span style={{ color: "#8a94a6", fontSize: 12 }}>
            01/{m}/{r.year} – {new Date(r.year, r.month, 0).getDate()}/{m}/{r.year}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, r) => {
        if (r.isFuture)
          return <span style={{ fontSize: 11, color: "#8a94a6" }}>Not yet available</span>;
        if (r.isCurrent)
          return (
            <span style={{ fontSize: 11, background: "#fff3cd", color: "#856404", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>
              Partial (In Progress)
            </span>
          );
        return (
          <span style={{ fontSize: 11, background: "#d1e7dd", color: "#0f5132", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>
            Available
          </span>
        );
      },
    },
    {
      title: "Download",
      key: "download",
      align: "center",
      render: (_, r) => {
        if (r.isFuture) return null;
        const loading = downloadingMonth === r.month;
        return (
          <button
            onClick={() => handleMonthDownload(r.month)}
            disabled={loading}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 14px",
              background: loading ? "#f8f9fa" : "#e8f4e8",
              border: `1.5px solid ${loading ? "#dee2e6" : "#28a74533"}`,
              borderRadius: 8,
              color: loading ? "#8a94a6" : "#28a745",
              fontWeight: 600, fontSize: 12,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? <Spinner size="sm" /> : <><FiDownload size={13} /> Excel</>}
          </button>
        );
      },
    },
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* Page header */}
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FiFileText size={22} color="#1b5a90" />
                  FIU Monthly Reports
                </h3>
                <ul className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to="/admin">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/admin/finance/overview">Finance Dashboard</Link></li>
                  <li className="breadcrumb-item active">FIU Monthly Reports</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Info banner */}
          <div style={{
            background: "#e8f0fe", border: "1px solid #c7d9fb", borderRadius: 10,
            padding: "12px 18px", marginBottom: 20,
            display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <FiAlertCircle size={18} color="#1b5a90" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 13, color: "#1b5a90" }}>
              <strong>VASP Monthly Reporting (FIU-IND)</strong> — Reports must be submitted by the
              <strong> 15th of the succeeding month</strong> to{" "}
              <a href="mailto:dydirector-15@fiuindia.gov.in" style={{ color: "#1b5a90" }}>
                dydirector-15@fiuindia.gov.in
              </a>.
              Each Excel contains <strong>Annexure-I</strong> (exchange overview) and{" "}
              <strong>Annexure-2</strong> (coin-level breakdown).
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">

              {/* ── Year Overview Table ─────────────────────────────────── */}
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header" style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FiCalendar size={18} color="#1b5a90" />
                    <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>
                      Monthly Reports — {year}
                    </span>
                  </div>
                  <button
                    onClick={loadMonths}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#8a94a6" }}
                    title="Refresh"
                  >
                    <FiRefreshCw size={15} />
                  </button>
                </div>

                <div className="card-body" style={{ padding: "0" }}>
                  {loadingMonths ? (
                    <div style={{ padding: 40, textAlign: "center" }}>
                      <Spin tip="Loading..." />
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={months}
                      rowKey="month"
                      pagination={false}
                      size="middle"
                      locale={{ emptyText: "No data" }}
                    />
                  )}
                </div>
              </div>

              {/* ── Custom Date Range Report ────────────────────────────── */}
              <div className="card">
                <div className="card-header" style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
                }}>
                  <FiChevronDown size={18} color="#6f42c1" />
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>
                    Generate Custom Report
                  </span>
                </div>

                <div className="card-body" style={{ padding: "20px 24px" }}>
                  <p style={{ fontSize: 13, color: "#8a94a6", marginBottom: 20 }}>
                    Generate a FIU/VASP report for any custom date range. Select the period and
                    optionally enter the manual PMLA compliance fields (STR counts) before downloading.
                  </p>

                  {/* Date pickers row */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
                    <div style={{ flex: "1 1 200px" }}>
                      <label style={labelStyle}>Start Date</label>
                      <input
                        type="date"
                        value={startDate}
                        max={endDate || undefined}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: "1 1 200px" }}>
                      <label style={labelStyle}>End Date</label>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate || undefined}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Manual PMLA fields */}
                  <div style={{
                    background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 10,
                    padding: "16px 18px", marginBottom: 20,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#8a94a6", textTransform: "uppercase", letterSpacing: 1 }}>
                        PMLA / Compliance Fields (Manual Entry)
                      </span>
                      <Tooltip
                        title={
                          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                            <strong>PMLA</strong> = Prevention of Money Laundering Act (India).<br />
                            These fields are <strong>manually entered</strong> by the compliance team — they cannot be auto-computed from the database.<br />
                            Enter the counts reported to FIU-IND for the selected period before downloading.
                          </div>
                        }
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 320 }}
                      >
                        <FiHelpCircle size={14} color="#8a94a6" style={{ cursor: "pointer", flexShrink: 0 }} />
                      </Tooltip>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                      <div style={{ flex: "1 1 160px" }}>
                        <label style={labelStyle}>
                          STRs Filed
                          <Tooltip
                            title="Suspicious Transaction Reports filed with FIU-IND under PMLA. An STR must be filed within 7 days of forming a suspicion about a transaction. Enter the total count filed during this reporting period."
                            placement="top"
                            overlayStyle={{ maxWidth: 300 }}
                          >
                            <FiHelpCircle size={12} color="#aab4be" style={{ marginLeft: 5, cursor: "pointer", verticalAlign: "middle" }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number" min={0}
                          value={strs}
                          onChange={(e) => setStrs(Math.max(0, Number(e.target.value)))}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: "1 1 160px" }}>
                        <label style={labelStyle}>
                          Priority STRs
                          <Tooltip
                            title="A subset of STRs flagged as high urgency — typically involving terrorism financing, proliferation financing, or transactions above ₹50 lakh with immediate suspicion. These must be reported within 24 hours."
                            placement="top"
                            overlayStyle={{ maxWidth: 300 }}
                          >
                            <FiHelpCircle size={12} color="#aab4be" style={{ marginLeft: 5, cursor: "pointer", verticalAlign: "middle" }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number" min={0}
                          value={priorityStrs}
                          onChange={(e) => setPriorityStrs(Math.max(0, Number(e.target.value)))}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: "1 1 160px" }}>
                        <label style={labelStyle}>
                          Typology STRs
                          <Tooltip
                            title="STRs filed based on a recognised money-laundering or terror-financing pattern (typology) — e.g. layering via multiple accounts, structuring to avoid reporting thresholds, or exchange-hopping. Enter the count that match a specific typology."
                            placement="top"
                            overlayStyle={{ maxWidth: 300 }}
                          >
                            <FiHelpCircle size={12} color="#aab4be" style={{ marginLeft: 5, cursor: "pointer", verticalAlign: "middle" }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number" min={0}
                          value={typologyStrs}
                          onChange={(e) => setTypologyStrs(Math.max(0, Number(e.target.value)))}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: "1 1 160px" }}>
                        <label style={labelStyle}>
                          TM Alerts (override)
                          <Tooltip
                            title={
                              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                                <strong>Transaction Monitoring Alerts</strong> received from the AML screening system (TRM, Notabene, Fireblocks) during this period.<br /><br />
                                Leave at <strong>0</strong> to let the system auto-count from the database (transactions with a non-zero risk score). Enter a manual value only if you want to override the auto-count.
                              </div>
                            }
                            placement="topRight"
                            overlayStyle={{ maxWidth: 300 }}
                          >
                            <FiHelpCircle size={12} color="#aab4be" style={{ marginLeft: 5, cursor: "pointer", verticalAlign: "middle" }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number" min={0}
                          value={alertsCount}
                          onChange={(e) => setAlertsCount(Math.max(0, Number(e.target.value)))}
                          style={inputStyle}
                          placeholder="0 = auto-compute"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Generate button */}
                  <button
                    onClick={handleCustomDownload}
                    disabled={generatingCustom || !startDate || !endDate}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "10px 24px",
                      background: generatingCustom || !startDate || !endDate ? "#e9ecef" : "#1b5a90",
                      border: "none", borderRadius: 8,
                      color: generatingCustom || !startDate || !endDate ? "#8a94a6" : "#fff",
                      fontWeight: 700, fontSize: 14,
                      cursor: generatingCustom || !startDate || !endDate ? "not-allowed" : "pointer",
                      transition: "background 0.2s",
                    }}
                  >
                    {generatingCustom ? (
                      <><Spinner size="sm" /> Generating Report…</>
                    ) : (
                      <><FiDownload size={16} /> Generate &amp; Download Excel</>
                    )}
                  </button>
                </div>
              </div>

            </div>

            {/* ── Right sidebar — year filter ─────────────────────────── */}
            <div className="list-layout__filters">
              <FilterPanel
                count={months.filter((m) => !m.isFuture).length}
                onClear={() => setYear(currentYear)}
              >
                <FilterPanel.Field label="Year">
                  <input
                    type="number"
                    min={2020}
                    max={currentYear}
                    value={year}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v >= 2020 && v <= currentYear) setYear(v);
                    }}
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #dee2e6", borderRadius: 6, fontSize: 13 }}
                  />
                </FilterPanel.Field>
              </FilterPanel>

              {/* Reminder card */}
              <div style={{
                marginTop: 16, background: "#fff8e1",
                border: "1px solid #ffe082", borderRadius: 10, padding: "14px 16px",
              }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#856404", marginBottom: 6 }}>
                  Submission Reminder
                </div>
                <div style={{ fontSize: 12, color: "#6d5500", lineHeight: 1.5 }}>
                  Submit each month's report to FIU-IND by the <strong>15th of the following month</strong>.
                  <br /><br />
                  <a href="mailto:dydirector-15@fiuindia.gov.in" style={{ color: "#6d5500", fontWeight: 600 }}>
                    dydirector-15@fiuindia.gov.in
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600,
  color: "#5a6474", marginBottom: 5,
};

const inputStyle = {
  width: "100%", padding: "8px 12px",
  border: "1px solid #dee2e6", borderRadius: 7,
  fontSize: 13, color: "#1a1a2e",
  outline: "none",
};

export default FiuMonthlyReports;
